/**
 * Public Catalog Service
 * No authentication required - for guest/customer browsing
 */
import api from '../../config/axiosConfig';

export interface CatalogItem {
  id: number;
  sku: string;
  name: string;
  category: string;
  price: number;  // Backend returns 'price' field (with 40% markup for public)
  description?: string;
  barcode?: string;
  has_warranty: boolean;
  image?: {
    type: 'url' | 'upload' | 'placeholder';
    url: string;  // Backend always sends URL (external link or /api/v1/items/{id}/image)
    thumbnail_url?: string;
  };
  in_stock: boolean;
  unit?: string;
}

export interface CatalogCategory {
  name: string;      // Backend returns "name" instead of "category"
  icon: string;
  count: number;
}

export interface CatalogFilters {
  category?: string;
  search?: string;
  min_price?: number;
  max_price?: number;
  in_stock_only?: boolean;
  page?: number;
  page_size?: number;
}

/**
 * Browse all items in public catalog (no auth required)
 */
export const browsePublicCatalog = async (filters: CatalogFilters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.category) params.append('category', filters.category);
  if (filters.search) params.append('search', filters.search);
  if (filters.min_price) params.append('min_price', filters.min_price.toString());
  if (filters.max_price) params.append('max_price', filters.max_price.toString());
  if (filters.in_stock_only !== undefined) params.append('in_stock_only', filters.in_stock_only.toString());
  params.append('page', (filters.page || 1).toString());
  params.append('page_size', (filters.page_size || 20).toString());

  const response = await api.get(`/catalog/items?${params.toString()}`);
  return response.data;
};

/**
 * Get item details by ID (no auth required)
 */
export const getPublicItemDetails = async (itemId: number) => {
  const response = await api.get(`/catalog/items/${itemId}`);
  return response.data;
};

/**
 * Get all available categories with icons (no auth required)
 */
export const getPublicCategories = async (): Promise<CatalogCategory[]> => {
  const response = await api.get('/catalog/categories');
  return response.data;
};

/**
 * Get search suggestions (no auth required)
 */
export const getSearchSuggestions = async (query: string) => {
  const response = await api.get(`/catalog/search-suggestions?q=${encodeURIComponent(query)}`);
  return response.data;
};

/**
 * Check item availability (no auth required)
 */
export const checkItemAvailability = async (itemId: number) => {
  const response = await api.get(`/catalog/availability/${itemId}`);
  return response.data;
};

/**
 * Get category placeholder image (no auth required)
 */
export const getCategoryPlaceholder = (category: string) => {
  return `/catalog/placeholder/${encodeURIComponent(category)}`;
};
